import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { salesApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

/**
 * FR-SALES-03.07 — Customer segments (RFM).
 * Shows distribution + lets you recalculate.
 */
export function SegmentsPage() {
  const qc = useQueryClient();
  const [lookbackDays, setLookbackDays] = useState(90);

  const q = useQuery({
    queryKey: ['customer-segments'],
    queryFn: () => salesApi.getSegmentsSummary(),
  });

  const recalcMut = useMutation({
    mutationFn: () => salesApi.recalculateSegments({ lookbackDays, persistSegments: true }),
    onSuccess: (d) => {
      toast.success(
        `Recalculated. ${d.active} active, ${d.inactive} inactive across ${d.totalCustomers} customers.`,
        { duration: 6000 }
      );
      qc.invalidateQueries({ queryKey: ['customer-segments'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalActive = q.data?.distribution.reduce((s: number, d: any) =>
    d.segment === 'Inactive' || d.segment === 'Unsegmented' ? s : s + d.count, 0) ?? 0;
  const totalAll = q.data?.distribution.reduce((s: number, d: any) => s + d.count, 0) ?? 0;

  return (
    <div className="p-6">
      <PageHeader
        title="Customer Segments"
        subtitle="FR-SALES-03.07 — RFM scoring: Recency × Frequency × Monetary"
      />

      <div className="card p-4 mb-4 flex gap-3 items-end">
        <div>
          <label className="label">Lookback days</label>
          <input type="number" min={7} max={730} className="input w-32" value={lookbackDays}
            onChange={(e) => setLookbackDays(parseInt(e.target.value) || 90)} />
        </div>
        <button className="btn-primary" disabled={recalcMut.isPending} onClick={() => recalcMut.mutate()}>
          {recalcMut.isPending ? 'Recalculating…' : 'Recalculate segments'}
        </button>
        {q.data?.lastSegmentedAt && (
          <div className="text-xs text-slate-500 ml-auto">
            Last run: {new Date(q.data.lastSegmentedAt).toLocaleString()}
          </div>
        )}
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> :
       q.data?.distribution.length === 0 ? (
        <div className="card p-4"><EmptyState message="No segments yet. Run the recalculation to assign customers." /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card overflow-x-auto">
            <h3 className="font-semibold text-sm p-3 border-b">Distribution</h3>
            <table className="table">
              <thead><tr><th>Segment</th><th className="text-right">Count</th><th className="text-right">% of active</th></tr></thead>
              <tbody>
                {(q.data?.distribution ?? []).map((d: any) => {
                  const pctBase = d.segment === 'Inactive' || d.segment === 'Unsegmented' ? totalAll : totalActive;
                  const pct = pctBase > 0 ? (d.count / pctBase) * 100 : 0;
                  return (
                    <tr key={d.segment}>
                      <td>
                        <SegmentBadge name={d.segment} />
                      </td>
                      <td className="text-right font-mono">{d.count}</td>
                      <td className="text-right text-xs text-slate-500">
                        {d.segment === 'Inactive' || d.segment === 'Unsegmented'
                          ? `${pct.toFixed(1)}% of all`
                          : `${pct.toFixed(1)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card p-4 text-xs text-slate-600">
            <h3 className="font-semibold text-sm mb-2 text-slate-800">Segment guide</h3>
            <dl className="space-y-2">
              <div><dt className="font-semibold text-emerald-700">Champions</dt><dd>Best customers — buy often, recently, and spend most. Reward them.</dd></div>
              <div><dt className="font-semibold text-emerald-600">Loyal</dt><dd>Regular buyers, consistent history. Upsell/cross-sell opportunity.</dd></div>
              <div><dt className="font-semibold text-blue-600">Potential Loyalists</dt><dd>Recent buyers with growing frequency. Nurture.</dd></div>
              <div><dt className="font-semibold text-blue-500">New Customers</dt><dd>First-time or recent starters. Onboarding + follow-up.</dd></div>
              <div><dt className="font-semibold text-amber-600">Needs Attention</dt><dd>Mid-tier, declining. Re-engagement campaign.</dd></div>
              <div><dt className="font-semibold text-amber-700">At Risk</dt><dd>Were buying regularly, dropped off. Win-back email.</dd></div>
              <div><dt className="font-semibold text-orange-700">Can't Lose Them</dt><dd>Big spenders gone quiet. High-priority outreach.</dd></div>
              <div><dt className="font-semibold text-slate-500">Hibernating / Lost</dt><dd>Minimal activity. Low-effort reactivation only.</dd></div>
              <div><dt className="font-semibold text-slate-400">Inactive</dt><dd>No purchases in window. Not RFM-scored.</dd></div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

function SegmentBadge({ name }: { name: string }) {
  const cls = {
    'Champions': 'bg-emerald-100 text-emerald-800',
    'Loyal': 'bg-emerald-100 text-emerald-700',
    'Potential Loyalists': 'bg-blue-100 text-blue-700',
    'New Customers': 'bg-blue-50 text-blue-600',
    'Needs Attention': 'bg-amber-100 text-amber-700',
    'At Risk': 'bg-amber-100 text-amber-800',
    "Can't Lose Them": 'bg-orange-100 text-orange-700',
    'Hibernating': 'bg-slate-100 text-slate-600',
    'Lost': 'bg-slate-200 text-slate-500',
    'Inactive': 'bg-slate-100 text-slate-400',
    'Unsegmented': 'bg-slate-50 text-slate-400',
  }[name] ?? 'bg-slate-100 text-slate-700';
  return <span className={`badge ${cls} text-xs`}>{name}</span>;
}
